/**
 * Service requests / work orders, and the two registers they reference.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * A WORK ORDER REQUESTS SERVICES. IT DOES NOT DESCRIBE HOW ANY OF THEM IS
 * PERFORMED. There is no input field, reading, unit of measurement, formula,
 * calculation order, rounding rule, acceptance limit, chart, axis, Excel cell
 * mapping or specialised result layout in this file, and there must never be
 * one — not for the five initial tests and not for any test. What a requested
 * service carries about the science is exactly one honest fact: the
 * `templateReadiness` of the catalogue entry it names, forwarded unchanged, so
 * a request for a `not_ready` test says so rather than implying the system can
 * calculate anything.
 *
 * Three further ideas govern the shapes below, each of them an acceptance
 * criterion rather than a preference:
 *
 *  1. THE PROGRESS STATUS IS DERIVED, NEVER STORED. It is computed from the
 *     states of the individual requested services, exactly as a quotation's
 *     `expired` is computed from its validity date (decision D46). A stored
 *     progress column would be a second source of truth that something has to
 *     keep agreeing with the services, and the day it stopped agreeing a
 *     half-finished job would read as finished.
 *
 *  2. PARTIAL IS NOT COMPLETE. `partially_complete` exists precisely so that
 *     finishing some of the work can never be reported as finishing all of it,
 *     and the derivation below is the single place that distinction lives.
 *
 *  3. CANCELLATION DELETES NOTHING. It is a stored fact with a reason, an
 *     actor and a timestamp; the number, the dates and every completed service
 *     survive it untouched.
 */
// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------
/**
 * How urgently the client asked for the work.
 *
 * A COMMERCIAL statement, made by whoever took the request. It changes the
 * order a laboratory chooses to work in; it changes nothing about any test.
 */
export const WORK_ORDER_PRIORITY = {
    NORMAL: 'normal',
    HIGH: 'high',
    URGENT: 'urgent',
};
export const ALL_WORK_ORDER_PRIORITIES = Object.freeze(Object.values(WORK_ORDER_PRIORITY));
// ---------------------------------------------------------------------------
// The state of ONE requested service
// ---------------------------------------------------------------------------
/**
 * Where one requested service has got to.
 *
 * This is the only progress that is STORED anywhere in the module, and each
 * value is set by a named person recording what happened — never by the
 * system inferring it and never because a button was pressed (CLAUDE.md
 * section 8).
 *
 * `CANCELLED` stops unexecuted work. It is not a deletion: the line keeps its
 * number, its price and its reason, and a service that was already `COMPLETE`
 * when the request was cancelled STAYS complete, because it was.
 */
export const WORK_ORDER_SERVICE_STATE = {
    /** Requested, not started. The state every service begins in. */
    REQUESTED: 'requested',
    IN_PROGRESS: 'in_progress',
    COMPLETE: 'complete',
    CANCELLED: 'cancelled',
};
export const ALL_WORK_ORDER_SERVICE_STATES = Object.freeze(Object.values(WORK_ORDER_SERVICE_STATE));
/**
 * The transitions a person may record, and the only ones.
 *
 * `COMPLETE` and `CANCELLED` are terminal. Reopening finished work is not an
 * edit to this row; it is a retest, which is an attempt with its own reason in
 * Phase 8, and quietly flipping a completed service back to `in_progress`
 * would erase the fact that it had ever been finished.
 */
export const WORK_ORDER_SERVICE_TRANSITIONS = Object.freeze({
    [WORK_ORDER_SERVICE_STATE.REQUESTED]: Object.freeze([
        WORK_ORDER_SERVICE_STATE.IN_PROGRESS,
        WORK_ORDER_SERVICE_STATE.COMPLETE,
        WORK_ORDER_SERVICE_STATE.CANCELLED,
    ]),
    [WORK_ORDER_SERVICE_STATE.IN_PROGRESS]: Object.freeze([
        WORK_ORDER_SERVICE_STATE.COMPLETE,
        WORK_ORDER_SERVICE_STATE.CANCELLED,
    ]),
    [WORK_ORDER_SERVICE_STATE.COMPLETE]: Object.freeze([]),
    [WORK_ORDER_SERVICE_STATE.CANCELLED]: Object.freeze([]),
});
export function workOrderServiceTransitionAllowed(from, to) {
    return WORK_ORDER_SERVICE_TRANSITIONS[from].includes(to);
}
/** Has this service been started at all? Completing counts as having started. */
export function workOrderServiceHasStarted(state) {
    return (state === WORK_ORDER_SERVICE_STATE.IN_PROGRESS ||
        state === WORK_ORDER_SERVICE_STATE.COMPLETE);
}
/**
 * The status of a work order, computed from what is actually true of it.
 *
 * ===================================================================
 * THIS FUNCTION IS WHERE "PARTIAL IS NOT COMPLETE" LIVES.
 * ===================================================================
 *
 * It is the single implementation, shared by the backend that filters on it
 * and the interface that renders it, so the two cannot disagree. Read it in
 * order, because the order is the meaning:
 *
 *  - A CANCELLED request is cancelled, whatever was finished before. The work
 *    that was done is still recorded on the services themselves.
 *  - A request whose services are ALL complete is complete. `total === 0` is
 *    deliberately excluded: a request with nothing on it has finished nothing,
 *    and reporting it complete would be the emptiest possible false claim.
 *  - A request with SOME complete is `partially_complete` — never `complete`,
 *    however many of the rest are cancelled or outstanding.
 *  - A request with work started but none finished is `in_progress`.
 *  - Otherwise no work on any service line has been recorded as started, and
 *    the word for that is `awaiting_sample`. REGISTERING A REQUEST IS NOT
 *    EVIDENCE THAT A SAMPLE ARRIVED.
 *
 * Only the service lines feed this. The sample register (Phase 7) and the
 * assignments, results and reports after it do not, so a request whose samples
 * are booked in still reads `awaiting_sample` until a service is started —
 * `PROJECT_DOCUMENTATION.md` limitation 71, a workflow question for the owner.
 *
 * `new` is in the status model and is deliberately not produced here. It would
 * be indistinguishable from `awaiting_sample`, and of the two the stronger
 * statement is the one that says no work has begun.
 */
export function deriveWorkOrderStatus(input) {
    if (input.isCancelled)
        return 'cancelled';
    const { total, completed, started } = input.services;
    if (total > 0 && completed >= total)
        return 'complete';
    if (completed > 0)
        return 'partially_complete';
    if (started > 0)
        return 'in_progress';
    return 'awaiting_sample';
}
/** Is this request past the date the laboratory promised?
 *
 * Compared as calendar DATES in the laboratory's own day, for the same reason
 * a quotation's validity is: a job due on the 30th is not late during the
 * 30th, wherever the server happens to be.
 *
 * A request with NO due date is never overdue. Nobody promised a date, and
 * treating an empty field as one — today's, or any other — would invent a
 * commitment the laboratory never made (CLAUDE.md section 8).
 *
 * A complete or cancelled request is not overdue either: lateness describes
 * outstanding work.
 */
export function workOrderIsOverdue(input) {
    if (input.dueDate === null)
        return false;
    if (input.status === 'complete' || input.status === 'cancelled')
        return false;
    return input.dueDate < input.today;
}
/** May the services on this request still be changed? */
export function workOrderAcceptsScopeChange(status) {
    return status !== 'complete' && status !== 'cancelled';
}
/**
 * Does changing the scope of this request need an explicit acknowledgement of
 * the impact?
 *
 * Yes once any work has started. Before that, editing the list of services is
 * ordinary correction of a request nobody has acted on. Afterwards it is a
 * renegotiation: somebody is already working, the price the client agreed is
 * already fixed, and the date the laboratory promised was calculated against a
 * different amount of work. The acceptance criterion is that the person making
 * the change says so about all three (PLAN.md, Phase 6).
 */
export function workOrderScopeChangeNeedsAcknowledgement(status) {
    return status !== 'awaiting_sample';
}
/**
 * The three impacts a scope change after work has started must acknowledge.
 *
 * Three separate statements rather than one "I understand" checkbox, and every
 * one of them must be affirmed. A single confirmation would be clicked
 * without reading; three named consequences are what the person is actually
 * agreeing to, and each is recorded in the audit event.
 */
export const SCOPE_CHANGE_IMPACTS = Object.freeze(['price', 'deadline', 'workInProgress']);
// ---------------------------------------------------------------------------
// The merge gate
// ---------------------------------------------------------------------------
/**
 * Every field a requested service may carry, as the API returns it.
 *
 * The Phase 5 equivalent for a catalogue entry (`CATALOGUE_ITEM_FIELDS`) is
 * asserted against the real API response by a merge-gate test, and this list
 * exists so the same gate covers the shape Phase 6 added. If somebody adds a
 * reading, a unit, a formula or a chart to a requested service, the test goes
 * red before it reaches a laboratory.
 *
 * READ CLAUDE.md SECTION 4 BEFORE ADDING A NAME TO THIS LIST.
 */
export const WORK_ORDER_SERVICE_FIELDS = Object.freeze([
    'id',
    'lineNumber',
    'catalogueItemId',
    'quotationLineId',
    'itemCode',
    'description',
    'referenceStandard',
    'standardVersion',
    'department',
    'pricingUnit',
    'quantity',
    'cataloguePriceMinor',
    'agreementPriceMinor',
    'unitPriceMinor',
    'priceSource',
    'priceOverrideReason',
    'discountPercent',
    'discountAmountMinor',
    'subtotalMinor',
    'discountMinor',
    'totalMinor',
    /**
     * The readiness of the catalogue entry this service names, forwarded
     * unchanged. The ONLY thing a work order says about the science, and what it
     * says is that there is no specialised template.
     */
    'templateReadiness',
    /** Told plainly rather than left to be inferred from the line above. */
    'usesGeneralWorkflowOnly',
    'state',
    'startedAt',
    'completedAt',
    'completionNote',
    'cancelledAt',
    'cancellationReason',
    'notes',
]);
//# sourceMappingURL=work-orders.js.map